// Public homepage (desktop and phone). In production this page is server-rendered for search.
// Edutainment: micro dramas, college life, comedy and useful how-to shows. Tagline: "Dekho kuch naya har roz".
// Flow (modelled on how leading Indian short-video apps sell, in Kuch Naya's own brand):
// nav → hero + honest catalogue numbers → topic pills → Top 10 → why Kuch Naya → creators → viewer reviews
// → how it works → Plus + plans → get the app (QR on desktop) → FAQ → footer.
import { useState, type CSSProperties } from 'react';
import { useCatalog } from '../lib/catalog';
import type { Show } from '../lib/types';
import { Link, useLocation } from '../lib/router';
import { Icon, type IconName } from '../components/Icon';
import { PosterArt } from '../components/Poster';
import { AppBar, PublicFooter, PublicHeader, StoreButtons, goSection, useSectionFromQuery } from '../components/PublicChrome';

const FAQ: [string, string][] = [
  ['What is Kuch Naya?', 'An app of short shows you can watch in a few minutes: micro dramas, college stories, comedy, and useful how-tos on money, jobs, government work and more. Something new to watch every day.'],
  ['Is it free?', 'Yes. Every show has free episodes, and you can watch them without paying. Kuch Naya Plus unlocks every episode of every show and removes ads.'],
  ['What is Kuch Naya Plus?', 'Plus is the membership that unlocks every episode of every show, new episodes every week, offline downloads in the app and no ads. You can see the plan and price in the app before you pay.'],
  ['How does UPI AutoPay work?', 'You approve it once in your UPI app and Plus renews every month. We tell you at least 24 hours before every payment, and you can stop it any time.'],
  ['How do I cancel?', 'Go to Account and tap Cancel, or stop AutoPay in your UPI app. You keep Plus until the end of the month you paid for.'],
  ['Which languages are the videos in?', 'Most shows are in Hindi with English subtitles. Some shows, like spoken English, are in English. You can pick Hindi, English or both.'],
  ['Can I watch on my computer?', 'Yes. Log in on this website with the same mobile number and continue where you left off. Downloads for offline viewing are in the app.'],
];

const REASONS: { icon: IconName; title: string; text: string }[] = [
  { icon: 'grid', title: 'Many kinds of shows', text: 'Micro dramas, college life, comedy, money, jobs, government work and more.' },
  { icon: 'heart', title: 'Stories you relate to', text: 'Real people, real situations: family, friends, first jobs and hostel life.' },
  { icon: 'clock', title: '2 to 10 minutes', text: 'Finish an episode on a break, in the bus or before bed.' },
  { icon: 'spark', title: 'Fun and useful', text: 'Laugh, binge a story or pick up a skill. Something new every day.' },
];

// Sample reviews for the preview only. Replace with real Play Store / in-app reviews at launch.
const SAMPLE_REVIEWS: [string, string, string][] = [
  ['Rishta Pakka? had me hooked. I finished every episode in one evening.', 'Sunita', 'Lucknow'],
  ['Ghar Ki Comedy is literally my family. My mother laughs the loudest.', 'Ramesh', 'Indore'],
  ['Hostel Diaries took me straight back to college. Two minutes of pure nostalgia.', 'Pooja', 'Nagpur'],
  ['I watch a drama, then a money show. Fun and useful in the same app.', 'Imran', 'Patna'],
  ['Short, clean and relatable. Perfect for the bus ride home.', 'Kavya', 'Coimbatore'],
];

const scrollTo = goSection;

// Free vs Plus (no prices on the public website; the price is shown in the app before paying).
const COMPARE: [string, boolean | string, boolean | string][] = [
  ['Free episodes in every show', true, true],
  ['Every episode of every show', false, true],
  ['New episodes every week', 'Free ones', true],
  ['Download and watch offline (app)', false, true],
  ['No ads', false, true],
  ['Save shows, streaks and progress', true, true],
  ['Phone, tablet and computer', true, true],
];

export function Landing() {
  const { shows: SHOWS, episodes: EPISODES, top10: TOP10_IDS, topics: TOPICS } = useCatalog();
  const [open, setOpen] = useState<number | null>(0);
  const { query } = useLocation();
  useSectionFromQuery(query);
  const top = TOP10_IDS.map((id) => SHOWS.find((s) => s.id === id)).filter((s): s is Show => !!s);
  // Hero fan: the top three shows (works with any live catalogue).
  const heroShows = (top.length >= 3 ? top : SHOWS).slice(0, 3);
  const heroEp = heroShows[1] && EPISODES.find((e) => e.showId === heroShows[1].id && e.no === 1);
  const freeEpisodes = EPISODES.filter((e) => e.isFreePreview).length;

  // Creators, built from the catalogue (count of shows and episodes, main topic, first show).
  const creators = Object.values(
    SHOWS.reduce<Record<string, { name: string; shows: typeof SHOWS; topic: string }>>((acc, s) => {
      const c = acc[s.creator.id] || { name: s.creator.name, shows: [], topic: s.topic };
      c.shows.push(s);
      acc[s.creator.id] = c;
      return acc;
    }, {}),
  ).map((c) => ({
    ...c,
    episodes: EPISODES.filter((e) => c.shows.some((s) => s.id === e.showId)).length,
    topicName: TOPICS.find((t) => t.slug === c.topic)?.name || '',
  }))
    // Entertainment creators first, then by episodes; 6 so every grid row is full.
    .sort((x, y) => Number(['drama', 'college', 'comedy'].includes(y.topic)) - Number(['drama', 'college', 'comedy'].includes(x.topic)) || y.episodes - x.episodes)
    .slice(0, 6);

  return (
    <div className="landing lp">
      <PublicHeader />

      {/* Hero */}
      <section className="wrap lp-hero">
        <div className="lp-hero-copy">
          <span className="lp-eyebrow"><Icon name="spark" size={14} /> Dramas, comedy, college life and smart how-tos</span>
          <h1>Dekho kuch naya har roz<span className="dot" aria-hidden="true" /></h1>
          <p className="lp-sub"><b>Micro dramas, college stories and comedy</b>, plus useful shows on money, jobs and more. {EPISODES.length}+ short episodes you can watch in minutes.</p>
          <div className="lp-hero-ctas">
            <span className="lp-cta-label">Download the app</span>
            <StoreButtons />
            <Link to="/home" className="text-link lp-web-link"><Icon name="play" size={14} /> Or start watching free on the web</Link>
          </div>
          <ul className="lp-proof" aria-label="What you get">
            <li><b>{SHOWS.length}</b><span>shows</span></li>
            <li><b>{EPISODES.length}</b><span>episodes</span></li>
            <li><b>{freeEpisodes}</b><span>free to watch</span></li>
            <li><b>2</b><span>languages</span></li>
          </ul>
        </div>
        <div className="lp-hero-art" aria-hidden="true">
          {heroShows.length === 3 && heroEp && <div className="lp-fan">
            <span className="lp-fan-back left"><PosterArt show={heroShows[0]} size="md" /></span>
            <span className="lp-fan-back right"><PosterArt show={heroShows[2]} size="md" /></span>
            <span className="lp-phone">
              <span className="lp-phone-notch" />
              <span className="lp-phone-screen" style={{ '--poster': heroShows[1].color } as CSSProperties}>
                <span className="lp-phone-hook">{heroEp.hook}</span>
                <span className="lp-phone-play"><Icon name="play" size={18} /></span>
                <span className="lp-phone-meta"><b>{heroShows[1].title}</b><small>{TOPICS.find((t) => t.slug === heroShows[1].topic)?.name || 'Show'} · Ep 1{heroEp.isFreePreview ? ' · Free' : ''}</small></span>
                <span className="lp-phone-bar"><span /></span>
              </span>
            </span>
          </div>}
        </div>
      </section>

      {/* Topics */}
      <section id="topics" className="wrap l-section">
        <h2>What do you feel like watching?</h2>
        <div className="lp-pills">
          <Link to="/topics" className="lp-pill all"><Icon name="grid" size={16} /> All topics</Link>
          {TOPICS.map((t) => (
            <Link key={t.slug} to={`/topic/${t.slug}`} className="lp-pill">
              <span className="lp-pill-dot" style={{ background: t.color }}>{t.glyph}</span>{t.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Top 10 */}
      <section className="wrap l-section">
        <div className="rail-head"><h2>Top 10 on Kuch Naya this week</h2><Link to="/home" className="text-link">See all</Link></div>
        <ol className="rail-row top10 top10-lg">
          {top.map((s, i) => (
            <li key={s.id}>
              <Link to={`/show/${s.slug}`} className="top10-item">
                <span className="top10-num" aria-hidden="true">{i + 1}</span>
                <PosterArt show={s} size="md" rank={i + 1} />
              </Link>
            </li>
          ))}
        </ol>
      </section>

      {/* Why */}
      <section className="wrap l-section">
        <h2>Why people love Kuch Naya</h2>
        <div className="lp-reasons">
          {REASONS.map((r) => (
            <div key={r.title} className="lp-reason">
              <span className="lp-reason-icon"><Icon name={r.icon} size={22} /></span>
              <b>{r.title}</b>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Creators */}
      <section className="wrap l-section">
        <div className="rail-head"><h2>Meet the creators</h2></div>
        <ul className="lp-creators">
          {creators.map((c) => (
            <li key={c.name}>
              <Link to={`/show/${c.shows[0].slug}`} className="lp-creator">
                <span className="lp-creator-avatar" style={{ '--poster': c.shows[0].color } as CSSProperties}>{c.name[0]}</span>
                <b>{c.name}</b>
                <small className="lp-creator-topic">{c.topicName}</small>
                <small className="muted">{c.shows.length} {c.shows.length === 1 ? 'show' : 'shows'} · {c.episodes} episodes</small>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Reviews */}
      <section className="l-band lp-reviews-band">
        <div className="wrap l-section">
          <div className="rail-head"><h2>What viewers say</h2><span className="lp-sample" title="Placeholder reviews for the preview">Sample</span></div>
          <ul className="lp-reviews">
            {SAMPLE_REVIEWS.map(([text, name, city]) => (
              <li key={name} className="lp-review">
                <span className="lp-stars" aria-label="5 out of 5">{[0, 1, 2, 3, 4].map((i) => <Icon key={i} name="star" size={16} />)}</span>
                <p>“{text}”</p>
                <span className="lp-review-who"><span className="avatar sm">{name[0]}</span><span><b>{name}</b><small>{city}</small></span></span>
              </li>
            ))}
          </ul>
          <p className="lp-sample-note">Sample reviews for this preview. Real reviews from viewers replace them at launch.</p>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="wrap l-section">
        <h2>Start in a minute</h2>
        <ol className="steps lp-steps">
          <li><span className="step-n">1</span><b>Sign up with your mobile number</b><p>Enter your number, type the OTP, and you are in. Or use Google.</p></li>
          <li><span className="step-n">2</span><b>Watch free episodes</b><p>Every show has free episodes. Swipe up for the next one, just like reels.</p></li>
          <li><span className="step-n">3</span><b>Unlock everything with Plus</b><p>Every episode of every show, no ads, offline downloads. Pay with UPI AutoPay and stop any time.</p></li>
        </ol>
      </section>

      {/* What is Kuch Naya Plus (no prices on the website) */}
      <section id="plus" className="wrap l-section">
        <div className="lp-plus">
          <div className="lp-plus-intro">
            <span className="badge badge-plus lp-plus-badge">PLUS</span>
            <h2>What is Kuch Naya Plus?</h2>
            <p>Plus is our membership for people who want it all. Watch every episode of every show, get new episodes every week, download for offline viewing and never see an ad.</p>
            <ul className="lp-plus-perks">
              <li><Icon name="play" size={18} /> Every episode of every show</li>
              <li><Icon name="spark" size={18} /> New episodes every week</li>
              <li><Icon name="adfree" size={18} /> No ads</li>
              <li><Icon name="download" size={18} /> Download and watch offline</li>
            </ul>
            <p className="muted small">Pay with UPI AutoPay. We remind you 24 hours before every payment, and you can stop any time from the app or your UPI app. See the plan in the app.</p>
            <div className="row-wrap">
              <button type="button" className="btn btn-primary" onClick={() => scrollTo('app')}><Icon name="download" size={16} /> Get Plus in the app</button>
              <Link to="/home" className="btn btn-ghost">Try free episodes</Link>
            </div>
          </div>
          <div className="lp-compare" role="table" aria-label="Free and Plus compared">
            <div className="lp-compare-row head" role="row"><span role="columnheader">What you get</span><span role="columnheader">Free</span><span role="columnheader" className="plus-col">Plus</span></div>
            {COMPARE.map(([label, free, plus]) => (
              <div key={label} className="lp-compare-row" role="row">
                <span role="cell">{label}</span>
                <span role="cell" className={free === true ? 'yes' : free === false ? 'no' : 'part'}>{free === true ? <Icon name="check" size={18} /> : free === false ? '—' : free}</span>
                <span role="cell" className={'plus-col ' + (plus === true ? 'yes' : 'part')}>{plus === true ? <Icon name="check" size={18} /> : plus}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get the app */}
      <section id="app" className="wrap l-section">
        <div className="lp-app">
          <div className="lp-app-copy">
            <h2>Get the Kuch Naya app</h2>
            <p>Watch offline, get new-episode alerts and keep your streak going. Dekho kuch naya har roz.</p>
            <StoreButtons />
            <Link to="/home" className="text-link">Or watch on this website now <Icon name="right" size={16} /></Link>
          </div>
          <div className="lp-qr" aria-label="QR code for the app (added at launch)">
            <span className="lp-qr-box"><Icon name="phone" size={28} /><small>QR code to the store listing, added when the app is live</small></span>
            <small className="muted">On your phone? Open <b>kuchnaya.in</b></small>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="wrap l-section">
        <h2>Questions</h2>
        <div className="faq">
          {FAQ.map(([q, a], i) => (
            <div key={q} className="faq-item">
              <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
                {q}<Icon name={open === i ? 'up' : 'down'} size={18} />
              </button>
              {open === i && <p>{a}</p>}
            </div>
          ))}
        </div>
      </section>

      <PublicFooter />
      <AppBar />
    </div>
  );
}

